import { type Token, TokenType } from "../odata-types.ts";

export class Lexer {
    private input: string;
    private position: number = 0;
    private currentChar: string | null = null;

    constructor(input: string) {
        this.input = input;
        this.currentChar = input.length > 0 ? input[0] : null;
    }

    private advance(): void {
        this.position++;
        if (this.position < this.input.length) {
            this.currentChar = this.input[this.position];
        } else {
            this.currentChar = null;
        }
    }

    private skipWhitespace(): void {
        while (this.currentChar !== null && /\s/.test(this.currentChar)) {
            this.advance();
        }
    }

    private readIdentifier(): Token {
        const startPos = this.position;
        while (this.currentChar !== null && /[a-zA-Z0-9_]/.test(this.currentChar)) {
            this.advance();
        }

        const value = this.input.slice(startPos, this.position);

        if (this.currentChar === "-" && /^[0-9a-fA-F]+$/.test(value)) {
            const guidToken = this.tryReadGuid(startPos, value);
            if (guidToken) {
                return guidToken;
            }
        }

        switch (value) {
            case "eq":
                return { type: TokenType.EQUALS, value, position: startPos };
            case "ne":
                return { type: TokenType.NOT_EQUALS, value, position: startPos };
            case "gt":
                return { type: TokenType.GREATER_THAN, value, position: startPos };
            case "ge":
                return { type: TokenType.GREATER_THAN_OR_EQUALS, value, position: startPos };
            case "lt":
                return { type: TokenType.LESS_THAN, value, position: startPos };
            case "le":
                return { type: TokenType.LESS_THAN_OR_EQUALS, value, position: startPos };
            case "in":
                return { type: TokenType.IN, value, position: startPos };
            case "and":
                return { type: TokenType.AND, value, position: startPos };
            case "or":
                return { type: TokenType.OR, value, position: startPos };
            case "not":
                return { type: TokenType.NOT, value, position: startPos };
            case "any":
                return { type: TokenType.ANY, value, position: startPos };
            case "all":
                return { type: TokenType.ALL, value, position: startPos };
            case "contains":
                return { type: TokenType.CONTAINS, value, position: startPos };
            case "startswith":
                return { type: TokenType.STARTSWITH, value, position: startPos };
            case "endswith":
                return { type: TokenType.ENDSWITH, value, position: startPos };
            case "now":
                return { type: TokenType.NOW, value, position: startPos };
            case "sub":
                return { type: TokenType.SUB, value, position: startPos };
            case "add":
                return { type: TokenType.ADD, value, position: startPos };
            case "duration":
                return { type: TokenType.DURATION, value, position: startPos };
            case "true":
                return { type: TokenType.BOOLEAN, value, position: startPos };
            case "false":
                return { type: TokenType.BOOLEAN, value, position: startPos };
            case "null":
                return { type: TokenType.NULL, value, position: startPos };
            case "guid":
                return { type: TokenType.IDENTIFIER, value, position: startPos };
            default:
                return { type: TokenType.IDENTIFIER, value, position: startPos };
        }
    }

    private tryReadGuid(startPos: number, prefix: string): Token | null {
        const savedPosition = this.position;
        const savedChar = this.currentChar;

        let guidValue = prefix;

        while (this.currentChar !== null && /[0-9a-fA-F-]/.test(this.currentChar)) {
            guidValue += this.currentChar;
            this.advance();
        }

        const guidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

        if (guidPattern.test(guidValue)) {
            return { type: TokenType.STRING, value: guidValue, position: startPos };
        }

        this.position = savedPosition;
        this.currentChar = savedChar;

        return null;
    }

    private readString(): Token {
        const startPos = this.position;
        const quote = this.currentChar;
        this.advance();

        let value = "";

        while (this.currentChar !== null) {
            if (this.currentChar !== quote) {
                value += this.currentChar;
                this.advance();
            } else if (this.input[this.position + 1] === quote) {
                value += quote;
                this.advance();
                this.advance();
            } else {
                break;
            }
        }

        if (this.currentChar !== quote) {
            throw new Error(`Unterminated string at position ${startPos}`);
        }

        this.advance();
        return { type: TokenType.STRING, value, position: startPos };
    }

    private readNumber(): Token {
        const startPos = this.position;
        let value = "";

        while (this.currentChar !== null && /\d/.test(this.currentChar)) {
            value += this.currentChar;
            this.advance();
        }

        if (this.currentChar === "-" || (this.currentChar !== null && /[a-fA-F]/.test(this.currentChar))) {
            const guidToken = this.tryReadGuid(startPos, value);
            if (guidToken) {
                return guidToken;
            }
        }

        if (this.currentChar === ".") {
            value += ".";
            this.advance();

            while (this.currentChar !== null && /\d/.test(this.currentChar)) {
                value += this.currentChar;
                this.advance();
            }
        }

        return { type: TokenType.NUMBER, value, position: startPos };
    }

    private readDateTime(): Token {
        const startPos = this.position;
        let value = "";

        while (this.currentChar !== null && /[\d\-:TZ.]/.test(this.currentChar)) {
            value += this.currentChar;
            this.advance();
        }

        if (!/^\d{4}-\d{2}-\d{2}/.test(value)) {
            throw new Error(`Invalid datetime literal '${value}' at position ${startPos}`);
        }

        return { type: TokenType.STRING, value, position: startPos };
    }

    getNextToken(): Token {
        while (this.currentChar !== null) {
            if (/\s/.test(this.currentChar)) {
                this.skipWhitespace();
                continue;
            }

            if (/[a-zA-Z_]/.test(this.currentChar)) {
                return this.readIdentifier();
            }

            if (this.currentChar === "'" || this.currentChar === '"') {
                return this.readString();
            }

            if (/\d/.test(this.currentChar)) {
                if (/^\d{4}-/.test(this.input.slice(this.position))) {
                    return this.readDateTime();
                }

                return this.readNumber();
            }

            switch (this.currentChar) {
                case "(":
                    this.advance();

                    return { type: TokenType.LEFT_PAREN, value: "(", position: this.position - 1 };
                case ")":
                    this.advance();

                    return { type: TokenType.RIGHT_PAREN, value: ")", position: this.position - 1 };
                case ",":
                    this.advance();

                    return { type: TokenType.COMMA, value: ",", position: this.position - 1 };
                case ":":
                    this.advance();

                    return { type: TokenType.COLON, value: ":", position: this.position - 1 };
                case "/":
                    this.advance();

                    return { type: TokenType.SLASH, value: "/", position: this.position - 1 };
                default:
                    throw new Error(`Unexpected character '${this.currentChar}' at position ${this.position}`);
            }
        }

        return { type: TokenType.EOF, value: "", position: this.position };
    }

    tokenize(): Token[] {
        const tokens: Token[] = [];
        let token = this.getNextToken();

        while (token.type !== TokenType.EOF) {
            tokens.push(token);
            token = this.getNextToken();
        }

        tokens.push(token);

        return tokens;
    }
}
